/**
 * sitemapLayout — tidy tree layout for the sitemap, ported from frontjutsu's
 * d3-hierarchy + d3-flextree approach (the algorithm the user asked to reuse).
 *
 * Pure: takes xyflow-ish nodes (with measured size + position) + edges and
 * returns nodes with NEW positions; the caller applies them (Tidy button /
 * Auto-arrange). Top-down (TB). A synthetic root joins the forest so multiple
 * top-level pages lay out side by side. Each node's parent is the DETERMINISTIC
 * `navParentOf` (shared with the renderer), and cycles / dangling parents are
 * grounded to the synthetic root so the d3 stratify never throws.
 *
 * d3-flextree gives node CENTER coordinates; we convert to xyflow's top-left
 * origin and normalise so the tree sits at a small canvas-positive offset.
 */
import { stratify } from "d3-hierarchy";
import { flextree } from "d3-flextree";
import { navParentOf, type NavEdge } from "@website-builder/schemas";

export interface LayoutNode {
  readonly id: string;
  readonly position: { readonly x: number; readonly y: number };
  readonly width?: number | undefined;
  readonly height?: number | undefined;
  readonly measured?: { width?: number; height?: number } | undefined;
}

export interface LayoutOptions {
  /** Horizontal gap between sibling subtrees. */
  readonly hGap?: number;
  /** Vertical gap between parent and child rows. */
  readonly vGap?: number;
  /** Top-left origin the laid-out tree is shifted to. */
  readonly originX?: number;
  readonly originY?: number;
}

const ROOT = "__sitemap_root__";
const DEFAULT_W = 320;
const DEFAULT_H = 480;

interface Datum {
  id: string;
  parentId?: string;
}

/**
 * Build a cycle-free parent map: a node keeps its `navParentOf` parent only if
 * that parent is "grounded" (its own chain reaches a parent-less node). Nodes
 * in or under a cycle, or pointing at an absent page, attach to the synthetic
 * root instead.
 */
function groundedParents(
  nodes: readonly LayoutNode[],
  edges: readonly NavEdge[],
): Map<string, string> {
  const present = new Set(nodes.map((n) => n.id));
  const raw = new Map<string, string>();
  for (const n of nodes) {
    const p = navParentOf(edges, n.id);
    if (p && present.has(p)) raw.set(n.id, p);
  }
  // Fixpoint: a node is grounded if it has no parent, or its parent is grounded.
  const grounded = new Set<string>();
  let changed = true;
  while (changed) {
    changed = false;
    for (const n of nodes) {
      if (grounded.has(n.id)) continue;
      const p = raw.get(n.id);
      if (p === undefined || grounded.has(p)) {
        grounded.add(n.id);
        changed = true;
      }
    }
  }
  const out = new Map<string, string>();
  for (const n of nodes) {
    const p = raw.get(n.id);
    out.set(n.id, p !== undefined && grounded.has(n.id) ? p : ROOT);
  }
  return out;
}

/**
 * Stable key over the INPUTS that determine a sitemap layout: the node set, each
 * node's measured size, and the parent/child edges — but deliberately NOT
 * positions. Auto-arrange re-runs only when this key changes, which means a page
 * that grew (e.g. columns added → taller block) re-tidies so it stops overlapping
 * the rows below, while the layout's own position updates don't retrigger it (no
 * loop). Sizes use the same fallbacks/precedence as `layoutSitemap` itself.
 */
export function layoutInputKey(
  nodes: readonly LayoutNode[],
  edges: readonly { readonly source: string; readonly target: string }[],
): string {
  const ns = nodes
    .map((n) => {
      const w = Math.round(n.measured?.width ?? n.width ?? DEFAULT_W);
      const h = Math.round(n.measured?.height ?? n.height ?? DEFAULT_H);
      return `${n.id}:${w}x${h}`;
    })
    .sort()
    .join(",");
  const es = edges
    .map((e) => `${e.source}>${e.target}`)
    .sort()
    .join(",");
  return `${ns}|${es}`;
}

export function layoutSitemap<N extends LayoutNode>(
  nodes: readonly N[],
  edges: readonly NavEdge[],
  opts: LayoutOptions = {},
): N[] {
  if (nodes.length === 0) return [...nodes];

  const hGap = opts.hGap ?? 60;
  const vGap = opts.vGap ?? 140;
  const originX = opts.originX ?? 40;
  const originY = opts.originY ?? 40;

  const sizeOf = (n: LayoutNode) => ({
    w: n.measured?.width ?? n.width ?? DEFAULT_W,
    h: n.measured?.height ?? n.height ?? DEFAULT_H,
  });
  const sizeById = new Map(nodes.map((n) => [n.id, sizeOf(n)] as const));
  const parent = groundedParents(nodes, edges);

  const data: Datum[] = [{ id: ROOT }];
  for (const n of nodes) data.push({ id: n.id, parentId: parent.get(n.id) });

  const root = stratify<Datum>()
    .id((d) => d.id)
    .parentId((d) => d.parentId)(data);

  const layout = flextree<Datum>({})
    .nodeSize((node) => {
      if (node.data.id === ROOT) return [0, 0];
      const s = sizeById.get(node.data.id)!;
      return [s.w, s.h + vGap];
    })
    .spacing(hGap);

  const laid = layout(root);

  // Convert flextree coords → xyflow top-left. d3-flextree uses an ASYMMETRIC
  // convention: `node.x` is the box CENTER (left = x - w/2) but `node.y` is the
  // box TOP (top = y, bottom = y + ySize; child.y = parent.y + parent.ySize). So
  // the vertical position is `node.y` directly — subtracting h/2 (treating y as a
  // center) pulls every row up by half its height and makes tall pages overlap the
  // row below. The vGap baked into ySize (`s.h + vGap`) then sits as clear space
  // BELOW each node, giving an exact vGap between a parent and its children.
  const pos = new Map<string, { x: number; y: number }>();
  let minX = Infinity;
  let minY = Infinity;
  laid.each((node) => {
    if (node.data.id === ROOT) return;
    const s = sizeById.get(node.data.id)!;
    const left = node.x - s.w / 2;
    const top = node.y;
    pos.set(node.data.id, { x: left, y: top });
    if (left < minX) minX = left;
    if (top < minY) minY = top;
  });

  const dx = originX - (Number.isFinite(minX) ? minX : 0);
  const dy = originY - (Number.isFinite(minY) ? minY : 0);

  return nodes.map((n) => {
    const p = pos.get(n.id);
    if (!p) return n;
    return { ...n, position: { x: p.x + dx, y: p.y + dy } };
  });
}
